/**
 * Modal "Editar Configurações eSocial" (#639)
 * Edita, na ficha da empresa, os cinco campos de envio ao eSocial. Manda à rota só o que mudou.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  companyService,
  type EsocialConfigAtual,
  type EsocialConfigMudancas,
} from '../../../../services/companyService';
import { ApiRequestError } from '../../../../types/common';
import {
  DOCUMENT_TYPE_LABELS,
  ESOCIAL_SEND_MODE_LABELS,
  type DocumentType,
  type ESocialSendMode,
} from '../../../../types/company';

/** O que está gravado hoje; a API devolve `null` para número, validade e início vazios. */
export interface EsocialConfigGravada {
  documentType?: string | null;
  documentNumber?: string | null;
  ecacExpirationDate?: string | null;
  esocialSendMode?: string | null;
  conditionStartDate?: string | null;
}

interface EsocialConfigModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  config: EsocialConfigGravada | undefined;
  /** Chamado com a configuração devolvida pelo servidor, antes de o modal fechar. */
  onSalvo: (atual: EsocialConfigAtual) => void;
}

interface Valores {
  documentType: string;
  documentNumber: string;
  ecacExpirationDate: string;
  esocialSendMode: string;
  conditionStartDate: string;
}

type CampoDeData = 'ecacExpirationDate' | 'conditionStartDate';

const TEXTO_FALHA_GERAL = 'Não foi possível salvar agora. Seus dados continuam aqui; tente de novo.';

/** Na comparação, null, undefined e '' são o mesmo valor. */
const comoTexto = (valor: string | null | undefined): string => valor ?? '';

const valoresDe = (config: EsocialConfigGravada | undefined): Valores => ({
  documentType: comoTexto(config?.documentType),
  documentNumber: comoTexto(config?.documentNumber),
  ecacExpirationDate: comoTexto(config?.ecacExpirationDate),
  esocialSendMode: comoTexto(config?.esocialSendMode),
  conditionStartDate: comoTexto(config?.conditionStartDate),
});

/** Só as chaves que diferem do que abriu; campo que aceita vazio e ficou vazio vira `null`. */
const mudancasEntre = (inicial: Valores, atual: Valores): EsocialConfigMudancas => {
  const mudancas: EsocialConfigMudancas = {};
  if (atual.documentType !== inicial.documentType) {
    mudancas.documentType = atual.documentType as DocumentType;
  }
  if (atual.esocialSendMode !== inicial.esocialSendMode) {
    mudancas.esocialSendMode = atual.esocialSendMode as ESocialSendMode;
  }
  if (atual.documentNumber !== inicial.documentNumber) {
    mudancas.documentNumber = atual.documentNumber || null;
  }
  if (atual.ecacExpirationDate !== inicial.ecacExpirationDate) {
    mudancas.ecacExpirationDate = atual.ecacExpirationDate || null;
  }
  if (atual.conditionStartDate !== inicial.conditionStartDate) {
    mudancas.conditionStartDate = atual.conditionStartDate || null;
  }
  return mudancas;
};

/** Recusa do servidor (qualquer 4xx) traz a mensagem pronta; rede e 5xx, o texto de tentar de novo. */
const mensagemDaFalha = (err: unknown): string => {
  const recusa = err instanceof ApiRequestError && err.status !== undefined && err.status >= 400 && err.status < 500;
  return recusa ? err.message : TEXTO_FALHA_GERAL;
};

interface FormularioProps extends Omit<EsocialConfigModalProps, 'open'> {
  salvando: boolean;
  onSalvandoChange: (salvando: boolean) => void;
}

const Formulario: React.FC<FormularioProps> = ({
  onClose,
  companyId,
  config,
  onSalvo,
  salvando,
  onSalvandoChange,
}) => {
  const [inicial] = useState(() => valoresDe(config));
  const [valores, setValores] = useState<Valores>(inicial);
  const [incompletas, setIncompletas] = useState<Record<CampoDeData, boolean>>({
    ecacExpirationDate: false,
    conditionStartDate: false,
  });
  const [falha, setFalha] = useState<string | null>(null);

  const alterar = (campo: keyof Valores) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValores((anterior) => ({ ...anterior, [campo]: e.target.value }));
  };

  // O seletor nativo devolve '' enquanto a data está pela metade; só o gesto de limpar apaga.
  const alterarData = (campo: CampoDeData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValores((anterior) => ({ ...anterior, [campo]: e.target.value }));
    setIncompletas((anterior) => ({ ...anterior, [campo]: e.target.validity.badInput }));
  };

  const temDataIncompleta = incompletas.ecacExpirationDate || incompletas.conditionStartDate;

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (temDataIncompleta) return;
    const mudancas = mudancasEntre(inicial, valores);
    if (Object.keys(mudancas).length === 0) {
      onClose();
      return;
    }
    onSalvandoChange(true);
    setFalha(null);
    try {
      const resposta = await companyService.updateEsocialConfig(companyId, mudancas);
      onSalvo(resposta.data);
      onClose();
    } catch (err: unknown) {
      setFalha(mensagemDaFalha(err));
    } finally {
      onSalvandoChange(false);
    }
  };

  return (
    <form onSubmit={salvar} noValidate>
      <DialogTitle id="esocial-config-titulo">Editar Configurações eSocial</DialogTitle>
      <DialogContent>
        {falha && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {falha}
          </Alert>
        )}

        <Box component="section" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" component="h3" fontWeight={700} sx={{ mb: 2 }}>
            Documento
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                select
                fullWidth
                label="Tipo de Documento"
                value={valores.documentType}
                onChange={alterar('documentType')}
                disabled={salvando}
              >
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([valor, rotulo]) => (
                  <MenuItem key={valor} value={valor}>
                    {rotulo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField
                fullWidth
                label="Número do Documento"
                value={valores.documentNumber}
                onChange={alterar('documentNumber')}
                disabled={salvando}
                slotProps={{ htmlInput: { maxLength: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box component="section">
          <Typography variant="subtitle1" component="h3" fontWeight={700} sx={{ mb: 2 }}>
            Envio
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                select
                fullWidth
                label="Modo de Envio eSocial"
                value={valores.esocialSendMode}
                onChange={alterar('esocialSendMode')}
                disabled={salvando}
              >
                {Object.entries(ESOCIAL_SEND_MODE_LABELS).map(([valor, rotulo]) => (
                  <MenuItem key={valor} value={valor}>
                    {rotulo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Validade e-CAC"
                value={valores.ecacExpirationDate}
                onChange={alterarData('ecacExpirationDate')}
                onBlur={(e) =>
                  setIncompletas((anterior) => ({ ...anterior, ecacExpirationDate: e.target.validity.badInput }))
                }
                error={incompletas.ecacExpirationDate}
                helperText={incompletas.ecacExpirationDate ? 'Data incompleta' : undefined}
                disabled={salvando}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Início das Condições (S-2240)"
                value={valores.conditionStartDate}
                onChange={alterarData('conditionStartDate')}
                onBlur={(e) =>
                  setIncompletas((anterior) => ({ ...anterior, conditionStartDate: e.target.validity.badInput }))
                }
                error={incompletas.conditionStartDate}
                helperText={
                  incompletas.conditionStartDate
                    ? 'Data incompleta'
                    : 'Data de início das condições ambientais de trabalho'
                }
                disabled={salvando}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={salvando || temDataIncompleta}>
          {salvando ? <CircularProgress size={24} /> : 'Salvar'}
        </Button>
      </DialogActions>
    </form>
  );
};

const EsocialConfigModal: React.FC<EsocialConfigModalProps> = ({ open, onClose, ...resto }) => {
  // Fica aqui, e não no formulário, para o Dialog saber que não pode fechar no meio da gravação.
  const [salvando, setSalvando] = useState(false);

  const fecharSeLivre = () => {
    if (!salvando) onClose();
  };

  return (
    <Dialog open={open} onClose={fecharSeLivre} maxWidth="sm" fullWidth aria-labelledby="esocial-config-titulo">
      {/* O formulário monta a cada abertura e guarda o que estava gravado nesse instante. */}
      <Formulario onClose={onClose} salvando={salvando} onSalvandoChange={setSalvando} {...resto} />
    </Dialog>
  );
};

export default EsocialConfigModal;
